type JsonObject = Record<string, unknown>;

export const HTTP_METHODS = ["get", "post", "put", "patch", "delete"] as const;
const AUDIENCES = ["advertiser", "publisher"] as const;

type Audience = (typeof AUDIENCES)[number];

const isJsonObject = (value: unknown): value is JsonObject =>
	Boolean(value) && typeof value === "object" && !Array.isArray(value);

const readAudiences = (operation: JsonObject): Audience[] => {
	const value = operation["x-onward-audience"];
	if(value === undefined) { return []; }
	if(!Array.isArray(value) || value.length === 0) {
		throw new Error("x-onward-audience must be a non-empty array");
	}

	for(const audience of value) {
		if(typeof audience !== "string" || !AUDIENCES.includes(audience as Audience)) {
			throw new Error(`Unsupported x-onward-audience value: ${String(audience)}`);
		}
	}

	return value as Audience[];
};

const renderAudienceBadges = (audiences: Audience[]): string =>
	audiences
		.map(
			(audience) =>
				`<AudienceBadge audience="${audience}"></AudienceBadge>`,
		)
		.join(" ");

export const withAudienceBadges = <T extends JsonObject>(document: T): T => {
	const decorated = structuredClone(document);
	if(!isJsonObject(decorated.paths)) { return decorated; }

	for(const pathItem of Object.values(decorated.paths)) {
		if(!isJsonObject(pathItem)) { continue; }

		for(const method of HTTP_METHODS) {
			const operation = pathItem[method];
			if(!isJsonObject(operation)) { continue; }

			const audiences = readAudiences(operation);
			if(audiences.length === 0) { continue; }

			const description =
				typeof operation.description === "string" ? operation.description : "";
			operation.description = [renderAudienceBadges(audiences), description]
				.filter(Boolean)
				.join("\n\n");
		}
	}

	return decorated;
};
